import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Download, Plus } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Heading1 } from '@/components/shared/typography/Heading';
import { BodyMuted } from '@/components/shared/typography/BodyText';
import { ErrorText } from '@/components/shared/typography/ErrorText';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  CATALOG_FRAMEWORKS_BY_ID,
  CATALOG_META,
  CatalogId,
  catalogFrameworkLabel,
  catalogFrameworkDescription,
  type CatalogCategory,
  type CatalogFrameworkType,
  type CatalogIdType,
} from '@kirin/api-client/contracts/ai-instruction-catalog';
import {
  useAiInstructionCatalog,
  useAddCustomCatalogCategory,
  useEditCustomCatalogCategory,
  useEditBuiltinCatalogCategory,
  useRemoveCustomCatalogCategory,
  useSetCatalogCategoryEnabled,
  useSetCatalogFrameworkEnabled,
} from '@/hooks/api/useAiInstructionCatalog';
import {
  CategoryFormDialog,
  type CategoryFormSubmit,
} from './CategoryFormDialog';
import { CategoryDetailPanel } from './CategoryDetailPanel';

interface AiInstructionCatalogPageProps {
  /** Org scope when set; personal (account) scope when undefined. */
  organizationId?: string;
  /** Whether the viewer may add/edit/remove categories. */
  canEdit: boolean;
  /**
   * Back-link target (the Policies list) shown as a breadcrumb. Omit for the
   * top-level Secure AI Coding Enforcement page, which is reached directly from
   * the left menu and has no parent to return to.
   */
  policiesPath?: string;
  /**
   * Back-link label. Defaults to "Policies". The AI Agent Instructions catalog
   * is reached from the AI Agent Instructions sub-page, so it passes both a
   * `policiesPath` pointing back there and this matching label (KIR-4184).
   */
  backLabel?: string;
  /**
   * Which catalog to render (KIR-3816). Defaults to `secure_coding` (the full
   * four-framework catalog); `agent_instructions` renders the single-framework
   * AI Agent Instructions catalog.
   */
  catalog?: CatalogIdType;
  /**
   * Optional Off/Monitor mode control (Enforce greyed, KIR-5037) rendered above
   * the framework tabs. The Secure AI Coding Enforcement page passes its
   * policy-mode selector here; the AI Agent Instructions catalog leaves it
   * undefined (its mode control stays on the Policies page).
   */
  modeControl?: ReactNode;
  /**
   * Whether to render the catalog title as an in-page heading. Defaults to
   * true. The top-level catalog pages (Secure AI Coding Enforcement, Threat
   * Modeling) pass false: the shell PageHeader already shows the same title,
   * and a second h1 repeats it in Classic UI (KIR-5600).
   */
  showTitle?: boolean;
}

/** Trigger a client-side JSON file download. */
function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

const SEVERITY_ROWS = [
  { key: 'critical', label: 'Critical' },
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' },
] as const;

/**
 * KIR-3515: the "Manage catalog" page (Policies > AI Instruction Catalog).
 * Master/detail: framework tabs → category list (with enable/disable + filter)
 * → detail panel. Built-in categories are editable (severity/keywords/example,
 * name+reference locked); custom categories support full CRUD. Frameworks and
 * categories can each be enabled/disabled. Storage/UI only — no monitoring.
 */
export function AiInstructionCatalogPage({
  organizationId,
  canEdit,
  policiesPath,
  backLabel = 'Policies',
  catalog = CatalogId.SecureCoding,
  modeControl,
  showTitle = true,
}: AiInstructionCatalogPageProps): ReactElement {
  const { toast } = useToast();
  const { data, isLoading, error } = useAiInstructionCatalog(
    organizationId,
    catalog
  );

  const addMutation = useAddCustomCatalogCategory(organizationId, catalog);
  const editCustomMutation = useEditCustomCatalogCategory(
    organizationId,
    catalog
  );
  const editBuiltinMutation = useEditBuiltinCatalogCategory(
    organizationId,
    catalog
  );
  const removeMutation = useRemoveCustomCatalogCategory(
    organizationId,
    catalog
  );
  const toggleCategoryMutation = useSetCatalogCategoryEnabled(
    organizationId,
    catalog
  );
  const toggleFrameworkMutation = useSetCatalogFrameworkEnabled(
    organizationId,
    catalog
  );

  // Frameworks this catalog exposes; the first is the default tab.
  const frameworks = CATALOG_FRAMEWORKS_BY_ID[catalog];
  const [activeFramework, setActiveFramework] = useState<CatalogFrameworkType>(
    () => frameworks[0]
  );
  const catalogMeta = CATALOG_META[catalog];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCategory, setDialogCategory] = useState<CatalogCategory | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<CatalogCategory | null>(
    null
  );

  const categories = useMemo(() => data?.categories ?? [], [data?.categories]);
  const counts = data?.counts;

  const frameworkEnabled = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const f of data?.frameworks ?? []) map.set(f.framework, f.enabled);
    return map;
  }, [data?.frameworks]);

  const frameworkCategories = useMemo(
    () => categories.filter(c => c.framework === activeFramework),
    [categories, activeFramework]
  );

  const filteredCategories = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return frameworkCategories;
    return frameworkCategories.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        (c.reference ?? '').toLowerCase().includes(q)
    );
  }, [frameworkCategories, filter]);

  // Effective selection without a setState-in-effect: use the tracked id when it
  // is in the current (filtered) framework list, else fall back to the first.
  // `selectedId` is intentionally NOT synced to the fallback — every consumer
  // (detail panel, toggles, prev/next) routes through `selectedCategory`/
  // `selectedIndex` below, so the fallback stays self-consistent without an extra
  // render pass.
  const selectedCategory =
    filteredCategories.find(c => c.id === selectedId) ??
    filteredCategories[0] ??
    null;
  const selectedIndex = selectedCategory
    ? filteredCategories.findIndex(c => c.id === selectedCategory.id)
    : -1;

  const severityCounts = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const c of frameworkCategories) {
      acc[c.severity] = (acc[c.severity] ?? 0) + 1;
    }
    return acc;
  }, [frameworkCategories]);

  const handleDialogSubmit = async (
    payload: CategoryFormSubmit
  ): Promise<void> => {
    if (payload.kind === 'builtin' && dialogCategory) {
      await editBuiltinMutation.mutateAsync({
        builtinSlug: dialogCategory.id,
        edit: payload.data,
      });
      toast({ title: 'Category updated' });
      return;
    }
    if (payload.kind === 'custom' && dialogCategory) {
      await editCustomMutation.mutateAsync({
        categoryId: dialogCategory.id,
        category: payload.data,
      });
      toast({ title: 'Category updated' });
      return;
    }
    if (payload.kind === 'custom') {
      await addMutation.mutateAsync(payload.data);
      toast({ title: 'Category added' });
    }
  };

  const handleToggleCategory = (
    category: CatalogCategory,
    enabled: boolean
  ): void => {
    toggleCategoryMutation.mutate(
      { categoryId: category.id, enabled },
      {
        onError: e =>
          toast({
            title: 'Failed to update category',
            description: e instanceof Error ? e.message : undefined,
            variant: 'destructive',
          }),
      }
    );
  };

  const handleToggleFramework = (enabled: boolean): void => {
    toggleFrameworkMutation.mutate(
      { framework: activeFramework, enabled },
      {
        onError: e =>
          toast({
            title: 'Failed to update framework',
            description: e instanceof Error ? e.message : undefined,
            variant: 'destructive',
          }),
      }
    );
  };

  const handleConfirmDelete = (): void => {
    if (!deleteTarget) return;
    removeMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast({ title: 'Category deleted' });
        setDeleteTarget(null);
      },
      onError: e =>
        toast({
          title: 'Failed to delete category',
          description: e instanceof Error ? e.message : undefined,
          variant: 'destructive',
        }),
    });
  };

  const activeLabel = catalogFrameworkLabel(catalog, activeFramework);
  const activeFrameworkOn = frameworkEnabled.get(activeFramework) ?? true;
  const enabledInFramework = frameworkCategories.filter(c => c.enabled).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          {policiesPath && (
            <Link
              to={policiesPath}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="size-4" />
              {backLabel}
            </Link>
          )}
          {showTitle && (
            <Heading1 data-page-title>{catalogMeta.title}</Heading1>
          )}
          <BodyMuted>{catalogMeta.description}</BodyMuted>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              downloadJson('ai-instruction-catalog.json', {
                categories,
                counts,
              })
            }
          >
            <Download className="mr-1 size-4" />
            Export JSON
          </Button>
          {canEdit && (
            <Button
              size="sm"
              onClick={() => {
                setDialogCategory(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="mr-1 size-4" />
              Add category
            </Button>
          )}
        </div>
      </div>

      {error && (
        <ErrorText as="p">
          {error instanceof Error ? error.message : String(error)}
        </ErrorText>
      )}

      {isLoading ? (
        <Card>
          <CardContent>
            <BodyMuted>Loading…</BodyMuted>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="space-y-4">
            {/* KIR-3816: Off/Monitor mode control, above the framework tabs. */}
            {modeControl && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
                <div className="text-sm">
                  <span className="font-semibold">Enforcement mode</span>
                  <span className="ml-2 text-muted-foreground">
                    Sets how the whole catalog behaves. Default is Off.
                  </span>
                </div>
                {/* PolicyModeSelector is w-full; constrain it so it doesn't
                    stretch across the whole bar (mirrors the card's wrapper). */}
                <div className="w-full shrink-0 sm:w-[320px]">
                  {modeControl}
                </div>
              </div>
            )}
            <Tabs
              value={activeFramework}
              onValueChange={v => {
                setActiveFramework(v as CatalogFrameworkType);
                setSelectedId(null);
                setFilter('');
              }}
            >
              <TabsList>
                {frameworks.map(framework => (
                  <TabsTrigger
                    key={framework}
                    value={framework}
                    className="gap-1.5"
                  >
                    {catalogFrameworkLabel(catalog, framework)}
                    {/* KIR-3816: count in a bordered badge so a framework name
                        ending in a number (e.g. "OWASP Top 10") no longer runs
                        together with its count ("OWASP Top 10 10"). */}
                    <Badge
                      variant="outline"
                      className="px-1.5 py-0 font-mono text-[10px] leading-4"
                    >
                      {counts?.[framework] ?? 0}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <BodyMuted className="text-sm">
              {catalogFrameworkDescription(catalog, activeFramework)}
            </BodyMuted>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
              <div className="text-sm">
                <span className="font-semibold">{activeLabel}</span> framework —{' '}
                {enabledInFramework} of {frameworkCategories.length} categories
                on
              </div>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="framework-enabled"
                  className="text-sm text-muted-foreground"
                >
                  Framework enabled
                </Label>
                <Switch
                  id="framework-enabled"
                  checked={activeFrameworkOn}
                  disabled={!canEdit || toggleFrameworkMutation.isPending}
                  onCheckedChange={handleToggleFramework}
                  aria-label={`Toggle ${activeLabel} framework`}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-[minmax(0,320px)_1fr]">
              {/* Left: severity counts + filter + category list */}
              <div className="space-y-3">
                <div className="flex gap-4">
                  {SEVERITY_ROWS.filter(s => severityCounts[s.key]).map(s => (
                    <div key={s.key}>
                      <div className="text-lg font-semibold">
                        {severityCounts[s.key]}
                      </div>
                      <div className="text-xs uppercase text-muted-foreground">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>

                <Input
                  placeholder="Filter categories…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />

                <ul className="space-y-1">
                  {filteredCategories.map((category, i) => {
                    const isSelected = category.id === selectedCategory?.id;
                    return (
                      <li
                        key={category.id}
                        className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60 ${
                          isSelected ? 'bg-muted' : ''
                        }`}
                      >
                        <span className="w-5 shrink-0 text-xs text-muted-foreground">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedId(category.id)}
                          className={`min-w-0 flex-1 truncate text-left ${
                            category.enabled ? '' : 'text-muted-foreground'
                          }`}
                        >
                          {category.name}
                        </button>
                        <Switch
                          checked={category.enabled}
                          disabled={
                            !canEdit ||
                            (toggleCategoryMutation.isPending &&
                              toggleCategoryMutation.variables?.categoryId ===
                                category.id)
                          }
                          onCheckedChange={enabled =>
                            handleToggleCategory(category, enabled)
                          }
                          aria-label={`Toggle ${category.name}`}
                        />
                      </li>
                    );
                  })}
                  {filteredCategories.length === 0 && (
                    <li>
                      <BodyMuted className="px-2 py-3">
                        No categories match.
                      </BodyMuted>
                    </li>
                  )}
                </ul>
              </div>

              {/* Right: detail panel */}
              <div className="rounded-md border p-4">
                {selectedCategory ? (
                  <CategoryDetailPanel
                    category={selectedCategory}
                    catalog={catalog}
                    index={selectedIndex}
                    total={filteredCategories.length}
                    canEdit={canEdit}
                    togglePending={
                      toggleCategoryMutation.isPending &&
                      toggleCategoryMutation.variables?.categoryId ===
                        selectedCategory.id
                    }
                    deletePending={removeMutation.isPending}
                    onToggleEnabled={enabled =>
                      handleToggleCategory(selectedCategory, enabled)
                    }
                    onEdit={() => {
                      setDialogCategory(selectedCategory);
                      setDialogOpen(true);
                    }}
                    onExport={() =>
                      downloadJson(
                        `${selectedCategory.id}.json`,
                        selectedCategory
                      )
                    }
                    onDelete={
                      selectedCategory.isBuiltIn
                        ? undefined
                        : () => setDeleteTarget(selectedCategory)
                    }
                    onPrev={() => {
                      const prev = filteredCategories[selectedIndex - 1];
                      if (prev) setSelectedId(prev.id);
                    }}
                    onNext={() => {
                      const next = filteredCategories[selectedIndex + 1];
                      if (next) setSelectedId(next.id);
                    }}
                  />
                ) : (
                  <BodyMuted>Select a category to see details.</BodyMuted>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <CategoryFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category={dialogCategory}
        onSubmit={handleDialogSubmit}
        isPending={
          addMutation.isPending ||
          editCustomMutation.isPending ||
          editBuiltinMutation.isPending
        }
      />

      <ConfirmationDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete custom category?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" will be permanently removed from your catalog.`
            : ''
        }
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        isLoading={removeMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
